import {useCallback, useEffect, useState} from 'react';

import {recordVisit} from './recentsFrecency';
import type {PinnedItem} from './WorkstreamsSearch';

/**
 * /workstreams: where its recents live. /workstreams counts its
 * workstream visits here too, so people who switch over find recents already
 * ranked.
 */
export const RECENTS_STORAGE_KEY = 'workstreams5_recent_items';
// Pinned recents are always kept; unpinned ones are capped, lowest frecency
// dropping first (see recentsFrecency).
export const MAX_UNPINNED_RECENTS = 12;

export const loadRecents = (): PinnedItem[] => {
  try {
    const stored = localStorage.getItem(RECENTS_STORAGE_KEY);
    return stored ? JSON.parse(stored) : [];
  } catch {
    return [];
  }
};

export const saveRecents = (items: PinnedItem[]) => {
  try {
    localStorage.setItem(RECENTS_STORAGE_KEY, JSON.stringify(items));
  } catch {
    /* quota exceeded */
  }
};

/** Count a visit straight into storage, for pages that don't show recents. */
export const recordStoredVisit = (item: PinnedItem, now = Date.now()) =>
  saveRecents(recordVisit(loadRecents(), item, now, MAX_UNPINNED_RECENTS));

/**
 * Recents shared by every open tab. Each update applies to what's in storage
 * now, not this tab's copy, so tabs don't overwrite each other's visits and
 * pins; other tabs' writes (and /workstreams' visits) show up here as they land.
 */
export const useStoredRecents = () => {
  const [items, setItems] = useState<PinnedItem[]>(loadRecents);

  useEffect(() => {
    // Fires only for writes from other tabs; a null key means storage was cleared.
    const onStorage = (event: StorageEvent) => {
      if (event.key === RECENTS_STORAGE_KEY || event.key === null) {
        setItems(loadRecents());
      }
    };
    window.addEventListener('storage', onStorage);
    return () => window.removeEventListener('storage', onStorage);
  }, []);

  const update = useCallback((apply: (stored: PinnedItem[]) => PinnedItem[]) => {
    const next = apply(loadRecents());
    saveRecents(next);
    setItems(next);
  }, []);

  return [items, update] as const;
};
